import { useEffect, useState } from 'react'
import { useApiQuery } from 'grabbitbot-legacy-ui'
import { money, paymentReads } from '../../api/fee'
import type { Billing } from '../../api/types'
import { Notice, PageTitle, QueryState } from '../../components/common'
import { useProject } from '../../layout/projectContext'
import { COUNTRY_CODES, countryName } from '../../lib/countries'
import { useMoneyAction } from '../../lib/useMoneyAction'

const FIELDS: { key: string; label: string }[] = [
  { key: 'name', label: 'Name' },
  { key: 'state', label: 'State' },
  { key: 'city', label: 'City' },
  { key: 'postal_code', label: 'Postal code' },
  { key: 'line1', label: 'Address line 1' },
  { key: 'line2', label: 'Address line 2' },
]

/**
 * Port of `PaymentBillingViewComponent` (`projects/:projectId/payment/billing`):
 * the Stripe customer's billing address (`GET|PATCH …/payment/billing`,
 * snake_case keys as the backend relays them, b5.billing.modify). The
 * invoice e-mail is editable only when the backend says so
 * (`invoice_email_editable`).
 */
export function PaymentBillingPage() {
  const { projectId } = useProject()
  const q = useApiQuery(() => paymentReads.billing(projectId), [projectId])
  const [form, setForm] = useState<Billing | null>(null)
  const actions = useMoneyAction()

  useEffect(() => {
    if (q.data) setForm({ ...q.data })
  }, [q.data])

  const set = (key: string, value: string) => setForm((prev) => ({ ...(prev ?? {}), [key]: value }))

  const save = async () => {
    if (!form) return
    const body: Billing = {}
    for (const f of FIELDS) body[f.key] = String(form[f.key] ?? '')
    body.country = String(form.country ?? '')
    if (form.invoice_email_editable) body.invoice_email = String(form.invoice_email ?? '')
    const r = await actions.run('save', JSON.stringify(body), () => money.modifyBilling(projectId, body))
    if (r !== undefined) {
      if (r === null) actions.setFeedback('save', { tone: 'error', text: 'Saving the billing information failed.' })
      else actions.setFeedback('save', { tone: 'success', text: 'Billing information saved.' })
      q.refetch()
    }
  }

  return (
    <div className="portal-card">
      <PageTitle title="Billing" />
      <QueryState loading={q.loading && !form} error={q.error}>
        {form && (
          <form
            className="portal-form"
            aria-label="Billing information"
            onSubmit={(e) => {
              e.preventDefault()
              void save()
            }}
          >
            <label>
              Email
              <input
                type="email"
                value={String(form.invoice_email ?? form.email ?? '')}
                readOnly={!form.invoice_email_editable}
                onChange={(e) => set('invoice_email', e.target.value)}
              />
            </label>
            {FIELDS.slice(0, 1).map((f) => (
              <label key={f.key}>
                {f.label}
                <input value={String(form[f.key] ?? '')} onChange={(e) => set(f.key, e.target.value)} />
              </label>
            ))}
            <label>
              Country
              <select value={String(form.country ?? '')} onChange={(e) => set('country', e.target.value)}>
                <option value="">—</option>
                {COUNTRY_CODES.map((c) => (
                  <option key={c} value={c}>
                    {countryName(c)}
                  </option>
                ))}
              </select>
            </label>
            {FIELDS.slice(1).map((f) => (
              <label key={f.key}>
                {f.label}
                <input value={String(form[f.key] ?? '')} onChange={(e) => set(f.key, e.target.value)} />
              </label>
            ))}
            <button type="submit" disabled={actions.isBusy('save')}>
              {actions.isBusy('save') ? 'Saving…' : 'Save'}
            </button>
            <Notice feedback={actions.feedbackFor('save')} />
          </form>
        )}
      </QueryState>
    </div>
  )
}
